import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { workflowsApi } from '@/services/api';
import type { Workflow, WorkflowCreate, WorkflowUpdate, WorkflowsState } from '@/types';

const initialState: WorkflowsState = {
  items: [],
  loading: false,
  error: null,
  total: 0,
  page: 1,
  size: 10,
};

export const createWorkflow = createAsyncThunk(
  'workflows/createWorkflow',
  async (workflowData: WorkflowCreate) => {
    const response = await workflowsApi.createWorkflow(workflowData);
    return response;
  }
);

export const fetchWorkflows = createAsyncThunk(
  'workflows/fetchWorkflows',
  async ({ page = 1, size = 10, search, tag }: { page?: number, size?: number, search?: string, tag?: string } = {}) => {
    const response = await workflowsApi.getWorkflows(page, size, search, tag);
    return response;
  }
);

export const fetchWorkflow = createAsyncThunk(
  'workflows/fetchWorkflow',
  async (id: number) => {
    const response = await workflowsApi.getWorkflow(id);
    return response;
  }
);

export const updateWorkflow = createAsyncThunk(
  'workflows/updateWorkflow',
  async ({ id, workflowData }: { id: number, workflowData: WorkflowUpdate }) => {
    const response = await workflowsApi.updateWorkflow(id, workflowData);
    return response;
  }
);

export const deleteWorkflow = createAsyncThunk(
  'workflows/deleteWorkflow',
  async (id: number) => {
    await workflowsApi.deleteWorkflow(id);
    return id;
  }
);

const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create workflow
      .addCase(createWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkflow.fulfilled, (state, action: PayloadAction<Workflow>) => {
        state.loading = false;
        state.items.unshift(action.payload); // Add to beginning of list
        state.total += 1;
      })
      .addCase(createWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create workflow';
      })
      // Fetch workflows
      .addCase(fetchWorkflows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action: PayloadAction<{workflows: Workflow[], total: number, page: number, size: number}>) => {
        state.loading = false;
        state.items = action.payload.workflows;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.size = action.payload.size;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workflows';
      })
      // Fetch single workflow
      .addCase(fetchWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflow.fulfilled, (state, action: PayloadAction<Workflow>) => {
        state.loading = false;
        // Update or add the workflow to the list
        const existingIndex = state.items.findIndex(item => item.id === action.payload.id);
        if (existingIndex !== -1) {
          state.items[existingIndex] = action.payload;
        } else {
          state.items.unshift(action.payload);
        }
      })
      .addCase(fetchWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workflow';
      })
      // Update workflow
      .addCase(updateWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkflow.fulfilled, (state, action: PayloadAction<Workflow>) => {
        state.loading = false;
        // Update the workflow in the list
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update workflow';
      })
      // Delete workflow
      .addCase(deleteWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkflow.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        // Remove the workflow from the list
        state.items = state.items.filter(item => item.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete workflow';
      });
  },
});

export const { clearError } = workflowsSlice.actions;
export default workflowsSlice.reducer;
